/**
 * Registration Page Component
 * Handles registration with invitation codes.
 * When an invitation code is present the page adopts the visual theme
 * (background gradient, SIFT title colour, button colour, feature card)
 * of the invited subscription tier.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthService } from '../services/AuthService';
import { ErrorHandler } from '../utils/ErrorHandler';
import { GradientText } from './text/gradient-text';
import LoadingSpinner from './LoadingSpinner';
import './Login.css';
import './Register.css';

// ── Per-tier visual palette ───────────────────────────────────────────────────

interface TierPalette {
  bgGradient: string;
  cardBorder: string;
  cardGlow: string;
  titleGradient: string;
  buttonBg: string;
  buttonHover: string;
  accentColor: string;
  label: string;
  credits: number | null;
  features: string[];
}

const TIER_PALETTE: Record<string, TierPalette> = {
  free: {
    bgGradient: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    cardBorder: 'rgba(100,116,139,0.4)',
    cardGlow: 'rgba(100,116,139,0.06)',
    titleGradient: 'linear-gradient(90deg, #3b82f6 0%, #a855f7 50%, #ec4899 100%)',
    buttonBg: '#646cff',
    buttonHover: '#5558dd',
    accentColor: '#94a3b8',
    label: 'Free',
    credits: 10,
    features: [
      'Up to 3 active classification criteria',
      'Single-garment textile classification',
      'Web-based capture interface',
    ],
  },
  starter: {
    bgGradient: 'linear-gradient(135deg, #06111f 0%, #0f243d 100%)',
    cardBorder: 'rgba(59,130,246,0.55)',
    cardGlow: 'rgba(59,130,246,0.09)',
    titleGradient: 'linear-gradient(90deg, #93c5fd 0%, #60a5fa 50%, #3b82f6 100%)',
    buttonBg: '#2563eb',
    buttonHover: '#1d4ed8',
    accentColor: '#60a5fa',
    label: 'Starter',
    credits: 100,
    features: [
      'Up to 4 active classification criteria',
      'Single-garment textile classification',
      '€0.19 per additional credit',
    ],
  },
  professional: {
    bgGradient: 'linear-gradient(135deg, #0e0720 0%, #1b1145 100%)',
    cardBorder: 'rgba(167,139,250,0.55)',
    cardGlow: 'rgba(124,58,237,0.1)',
    titleGradient: 'linear-gradient(90deg, #c4b5fd 0%, #a855f7 50%, #818cf8 100%)',
    buttonBg: '#7c3aed',
    buttonHover: '#6d28d9',
    accentColor: '#a78bfa',
    label: 'Professional',
    credits: 500,
    features: [
      'Up to 5 active classification criteria',
      'Multi-garment classification',
      '€0.16 per additional credit',
    ],
  },
  business: {
    bgGradient: 'linear-gradient(135deg, #110b00 0%, #2a1d00 100%)',
    cardBorder: 'rgba(251,191,36,0.55)',
    cardGlow: 'rgba(251,191,36,0.07)',
    titleGradient: 'linear-gradient(90deg, #fde68a 0%, #fbbf24 50%, #f59e0b 100%)',
    buttonBg: '#d97706',
    buttonHover: '#b45309',
    accentColor: '#fbbf24',
    label: 'Business',
    credits: 2000,
    features: [
      'All 10 active classification criteria',
      'Multi-garment classification',
      'Analytics Data Dashboard',
      '€0.12 per additional credit',
    ],
  },
  platinum: {
    bgGradient: 'linear-gradient(135deg, #05140c 0%, #0c2517 100%)',
    cardBorder: 'rgba(52,211,153,0.55)',
    cardGlow: 'rgba(16,185,129,0.08)',
    titleGradient: 'linear-gradient(90deg, #6ee7b7 0%, #34d399 50%, #10b981 100%)',
    buttonBg: '#059669',
    buttonHover: '#047857',
    accentColor: '#34d399',
    label: 'Platinum',
    credits: 10000,
    features: [
      'Up to 15 active classification criteria',
      'Multi-garment classification',
      'Analytics Data Dashboard',
      'Up to 5 custom classification criteria',
      '€0.04 per additional credit',
    ],
  },
  enterprise: {
    bgGradient: 'linear-gradient(135deg, #10060e 0%, #220f1c 100%)',
    cardBorder: 'rgba(244,114,182,0.55)',
    cardGlow: 'rgba(236,72,153,0.08)',
    titleGradient: 'linear-gradient(90deg, #fbcfe8 0%, #f472b6 50%, #ec4899 100%)',
    buttonBg: '#be185d',
    buttonHover: '#9d174d',
    accentColor: '#f472b6',
    label: 'Enterprise',
    credits: null,
    features: [
      'Up to 15 active classification criteria',
      'Multi-garment classification',
      'Analytics Data Dashboard',
      'Up to 5 custom classification criteria',
      'Custom pricing & dedicated support',
    ],
  },
};

const DEFAULT_PALETTE = TIER_PALETTE.free;

function getExpiryCountdown(expiresAt: string | null): string | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `Expires in ${days} day${days !== 1 ? 's' : ''}`;
  if (hours > 0) return `Expires in ${hours} hour${hours !== 1 ? 's' : ''}`;
  return `Expires in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
}

// ─────────────────────────────────────────────────────────────────────────────

function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState<{ tier: string; expires_at: string | null } | null>(null);
  const [validatingCode, setValidatingCode] = useState(false);

  const authService = AuthService.getInstance();
  const errorHandler = ErrorHandler.getInstance();
  const invitationCode = searchParams.get('code');

  const palette: TierPalette = invitationInfo
    ? (TIER_PALETTE[invitationInfo.tier] ?? DEFAULT_PALETTE)
    : DEFAULT_PALETTE;

  useEffect(() => {
    if (invitationCode) {
      validateInvitationCode(invitationCode);
    }
  }, [invitationCode]);

  const validateInvitationCode = async (code: string) => {
    setValidatingCode(true);
    try {
      const response = await fetch(`/api/invitation-codes/${code}/validate`);
      const result = await response.json();
      if (result.valid) {
        setInvitationInfo({ tier: result.tier, expires_at: result.expires_at ?? null });
      } else {
        setError(result.message || 'Invalid invitation code');
      }
    } catch (err) {
      console.error('Failed to validate invitation code:', err);
      setError('Failed to validate invitation code');
    } finally {
      setValidatingCode(false);
    }
  };

  const handleRegister = async () => {
    setError(null);
    setIsLoading(true);
    try {
      if (invitationCode) {
        localStorage.setItem('invitation_code', invitationCode);
      }
      await authService.register();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        errorHandler.handleAuthError(err);
      } else {
        setError('Registration failed. Please try again.');
        errorHandler.handleError(new Error('Registration failed'), { component: 'auth' });
      }
      console.error('Registration error:', err);
      setIsLoading(false);
    }
  };

  if (validatingCode) {
    return (
      <div className="register-container">
        <div className="register-card">
          <LoadingSpinner size="large" message="Validating invitation..." />
        </div>
      </div>
    );
  }

  const hasInvite = !!invitationInfo;

  return (
    <div
      className="register-container"
      style={hasInvite ? { background: palette.bgGradient } : undefined}
    >
      <div
        className="register-card"
        data-invited={hasInvite ? 'true' : 'false'}
        style={hasInvite ? {
          borderTop: `3px solid ${palette.cardBorder}`,
          boxShadow: `0 8px 40px rgba(0,0,0,0.45), 0 0 60px ${palette.cardGlow}`,
          '--tier-btn-bg': palette.buttonBg,
          '--tier-btn-hover': palette.buttonHover,
        } as React.CSSProperties : undefined}
      >
        {/* SIFT Branding */}
        <div className="sift-hero">
          <div className="sift-title">
            <GradientText
              text="SIFT"
              gradient={hasInvite ? palette.titleGradient : 'linear-gradient(90deg, #3b82f6 0%, #a855f7 50%, #ec4899 100%)'}
            />
          </div>
        </div>

        {/* Triality Labs Attribution */}
        <div className="triality-attribution">
          <span className="powered-by-text">powered by</span>
          <a
            href="https://trialitylabs.com"
            target="_blank"
            rel="noopener noreferrer"
            className="triality-link"
          >
            <img
              src="/assets/trialitylabs_logo.png"
              alt="Triality Labs"
              className="triality-logo-small"
            />
          </a>
        </div>

        {/* Tier invitation card */}
        {hasInvite && (
          <div
            className="tier-invite-card"
            style={{ borderColor: palette.cardBorder, background: palette.cardGlow }}
          >
            <div className="tier-invite-header">
              <span className="tier-invite-eyebrow" style={{ color: palette.accentColor }}>
                You're invited
              </span>
              <div className="tier-invite-header-right">
                <span
                  className="tier-invite-badge"
                  style={{ color: palette.accentColor, borderColor: palette.cardBorder }}
                >
                  {palette.label} Plan
                </span>
                {(() => {
                  const countdown = getExpiryCountdown(invitationInfo?.expires_at ?? null);
                  if (!countdown) return null;
                  const isExpired = countdown === 'Expired';
                  return (
                    <span className={`tier-expiry-badge ${isExpired ? 'tier-expiry-expired' : 'tier-expiry-active'}`}>
                      {countdown}
                    </span>
                  );
                })()}
              </div>
            </div>

            <div className="tier-credits-row" style={{ color: palette.accentColor }}>
              {palette.credits !== null ? (
                <>
                  <span className="tier-credits-number">
                    {palette.credits.toLocaleString()}
                  </span>
                  <span className="tier-credits-unit">credits / month</span>
                </>
              ) : (
                <span className="tier-credits-number">Unlimited</span>
              )}
            </div>

            <ul className="tier-features-list">
              {palette.features.map((feature, i) => (
                <li key={i} className="tier-feature-item">
                  <span className="tier-feature-check" style={{ color: palette.accentColor }}>✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CTA */}
        <div className="login-form">
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}
          <button
            type="button"
            className="login-button"
            onClick={handleRegister}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="button-spinner"></span>
                Creating Account…
              </>
            ) : (
              hasInvite
                ? `Claim Your ${palette.label} Access`
                : 'Create SIFT Account'
            )}
          </button>
        </div>

        <div className="login-footer">
          <p>
            {hasInvite
              ? 'Complete registration to activate your invited plan'
              : 'Create your account to start classifying textiles'
            }
          </p>
          <p style={{ marginTop: '0.5rem' }}>
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              style={{
                background: 'none',
                border: 'none',
                color: hasInvite ? palette.accentColor : '#3b82f6',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
